<template lang="pug">
.engagement-strip(v-if="sentEmail")
    el-tooltip(:content="stepTooltip('sent')" placement="top" effect="dark" :disabled="!stepTooltip('sent')")
        .strip-step(:class="stepStateOf('sent')")
            i.fas.el-icon-fa-paper-plane
            span {{ $t('Sent') }}
    .strip-line(:class="lineStateOf('delivered')")
    el-tooltip(:content="stepTooltip('delivered')" placement="top" effect="dark" :disabled="!stepTooltip('delivered')")
        .strip-step(:class="stepStateOf('delivered')")
            i.fas.el-icon-fa-truck
            span {{ $t('Delivered') }}
    .strip-line(:class="lineStateOf('opened')")
    el-tooltip(:content="stepTooltip('opened')" placement="top" effect="dark" :disabled="!stepTooltip('opened')")
        .strip-step(:class="stepStateOf('opened')")
            i.fas.el-icon-fa-envelope-open
            span(v-if="sentEmail.opens > 1") {{ $t('Opened') }} ({{ sentEmail.opens }}×)
            span(v-else) {{ $t('Opened') }}
    .strip-line(:class="lineStateOf('clicked')")
    el-tooltip(:content="stepTooltip('clicked')" placement="top" effect="dark" :disabled="!stepTooltip('clicked')")
        .strip-step(:class="stepStateOf('clicked')")
            i.fas.el-icon-fa-mouse-pointer
            span(v-if="sentEmail.clicks > 1") {{ $t('Clicked') }} ({{ sentEmail.clicks }}×)
            span(v-else) {{ $t('Clicked') }}
    el-tooltip(v-if="hasAlertState" :content="alertTooltip" placement="top" effect="dark")
        .strip-step.strip-step--alert
            i.fas.el-icon-fa-exclamation-triangle
            span {{ alertLabel }}
.engagement-strip__empty(v-else-if="fallbackSentAt")
    small {{ $t('Sent · no tracking data') }}
//- The mail leaves in a queued job, so between composing and its tracker row
//- there is a moment with nothing to show. The steps hold their place, greyed,
//- instead of an empty band that jumps into a strip a second later.
.engagement-strip.engagement-strip--pending(v-else-if="isPending")
    .strip-step.strip-step--pending
        i.fas.el-icon-fa-paper-plane
        span {{ $t('Sending') }}
    .strip-line
    .strip-step.strip-step--idle
        i.fas.el-icon-fa-truck
        span {{ $t('Delivered') }}
    .strip-line
    .strip-step.strip-step--idle
        i.fas.el-icon-fa-envelope-open
        span {{ $t('Opened') }}
    .strip-line
    .strip-step.strip-step--idle
        i.fas.el-icon-fa-mouse-pointer
        span {{ $t('Clicked') }}
.engagement-strip(v-else-if="isFailed")
    .strip-step.strip-step--alert
        i.fas.el-icon-fa-exclamation-triangle
        span {{ $t('Not sent') }}
</template>

<i18n>
{
    "de": {
        "Sent": "Versendet",
        "Delivered": "Zugestellt",
        "Opened": "Geöffnet",
        "Clicked": "Geklickt",
        "Bounced": "Unzustellbar",
        "Marked as spam": "Als Spam markiert",
        "Sent · no tracking data": "Versendet · kein Tracking verfügbar",
        "Sending": "Wird gesendet",
        "Not sent": "Nicht gesendet",
        "Inferred from open / click": "Aus Öffnen / Klick abgeleitet",
        "Inferred from click": "Aus Klick abgeleitet",
        "Pending": "Ausstehend"
    }
}
</i18n>

<script>
import { timestamp2date } from '@/filters';

export default {
    name: 'EngagementStrip',
    props: {
        sentEmail: {
            type: Object,
            default: null,
        },
        fallbackSentAt: {
            type: [Number, String],
            default: null,
        },
        dateTimeFormat: {
            type: String,
            required: true,
        },
        // The message's own delivery status, so the strip can hold its place
        // while the mail is still on its way out.
        status: {
            type: String,
            default: null,
        },
    },
    computed: {
        isPending() {
            return ['queued', 'draft', 'sending'].indexOf(this.status) !== -1;
        },
        isFailed() {
            return this.status === 'failed';
        },
        hasAlertState() {
            const e = this.sentEmail;
            return !!(e && (e.bounced_permanent_at || e.bounced_temporary_at || e.complained_at));
        },
        alertLabel() {
            if (!this.sentEmail) return '';
            if (this.sentEmail.complained_at) return this.$t('Marked as spam');
            return this.$t('Bounced');
        },
        alertTooltip() {
            const e = this.sentEmail;
            if (!e) return '';
            const value = e.complained_at || e.bounced_permanent_at || e.bounced_temporary_at;
            return value ? timestamp2date(value, this.dateTimeFormat) : '';
        },
    },
    methods: {
        // Each step inherits "done" from any later step that's done, because
        // those are logical consequences. Concrete examples:
        //   - if the recipient opened the email, it must have been delivered
        //     (even if the SMTP provider never sent us a delivery webhook)
        //   - if the recipient clicked, it must have been opened (the click
        //     handler runs after page load)
        // So we report green for "Delivered" whenever any of delivered_at /
        // opened_at / clicked_at is set, and green for "Opened" whenever
        // opened_at or clicked_at is set.
        stepStateOf(step) {
            const e = this.sentEmail;
            if (!e) return 'strip-step--idle';
            switch (step) {
                case 'sent':
                    return 'strip-step--done';
                case 'delivered':
                    return (e.delivered_at || e.opened_at || e.clicked_at)
                        ? 'strip-step--done'
                        : 'strip-step--idle';
                case 'opened':
                    return (e.opened_at || e.clicked_at)
                        ? 'strip-step--done'
                        : 'strip-step--idle';
                case 'clicked':
                    return e.clicked_at ? 'strip-step--done' : 'strip-step--idle';
                default:
                    return 'strip-step--idle';
            }
        },
        stepTooltip(step) {
            const e = this.sentEmail;
            if (!e) return '';
            const key = step === 'sent' ? 'created_at' : `${step}_at`;
            const value = e[key];
            if (value) return timestamp2date(value, this.dateTimeFormat);
            // No direct timestamp for this step but a later step is done —
            // surface that the timing is inferred rather than confirmed.
            if (step === 'delivered' && (e.opened_at || e.clicked_at)) {
                return this.$t('Inferred from open / click');
            }
            if (step === 'opened' && e.clicked_at) {
                return this.$t('Inferred from click');
            }
            return '';
        },
        // A connector is "done" when the step on its right is done — i.e.
        // the lifecycle has progressed past that connector.
        lineStateOf(rightStep) {
            return this.stepStateOf(rightStep) === 'strip-step--done'
                ? 'strip-line--done'
                : '';
        },
    },
};
</script>

<style lang="sass" scoped>
.engagement-strip
    display: flex
    align-items: center
    flex-wrap: nowrap
    // The strip should stay on ONE line, full natural width. When the
    // parent row is narrow, the parent's flex-wrap moves the strip to
    // its own row instead of letting it break internally. No max-width
    // or overflow clipping here — content takes the space it needs.

.strip-step,
.strip-line
    flex-shrink: 0

.engagement-strip__empty
    color: var(--color-text-muted, #909399)
    font-style: italic
    font-size: 11px

.strip-step
    display: flex
    align-items: center
    gap: 4px
    padding: 2px 8px
    border-radius: 12px
    font-size: 11px
    line-height: 1.4
    background: rgba(0, 0, 0, 0.04)
    color: var(--color-text-muted, #909399)
    cursor: default
    transition: background-color 120ms ease-in-out, color 120ms ease-in-out
    i
        font-size: 11px

.strip-step--done
    background: rgba(76, 175, 80, 0.16)
    color: #2e7d32

.strip-step--idle
    background: rgba(0, 0, 0, 0.04)
    color: var(--color-text-muted, #909399)

.strip-step--alert
    background: rgba(244, 67, 54, 0.15)
    color: #c62828

// Still on its way out: the step holds its place and breathes, so the strip
// does not appear out of nowhere once the mail is tracked.
.strip-step--pending
    background: rgba(0, 0, 0, 0.04)
    color: var(--color-text-muted, #909399)
    animation: engagement-pending 1.4s ease-in-out infinite

@keyframes engagement-pending
    0%, 100%
        opacity: 0.55
    50%
        opacity: 1

@media (prefers-reduced-motion: reduce)
    .strip-step--pending
        animation: none

.strip-line
    flex: 0 0 12px
    height: 2px
    background: rgba(0, 0, 0, 0.08)
    margin: 0 4px
    transition: background-color 120ms ease-in-out
    &.strip-line--done
        background: rgba(76, 175, 80, 0.5)
</style>
